'use strict';

import getVideoId from 'get-video-id';
import Player from '@vimeo/player';
import parseBoolean from '../utils/parse-boolean';

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

let youtubeApiPromise;

function loadYouTubeAPI() {
    if (youtubeApiPromise) {
        return youtubeApiPromise;
    }

    youtubeApiPromise = new Promise(resolve => { // eslint-disable-line compat/compat
        if (window.YT?.Player) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';

        const previous = window.onYouTubeIframeAPIReady;

        window.onYouTubeIframeAPIReady = () => {
            previous?.();
            resolve();
        };

        document.head.appendChild(script);
    });

    return youtubeApiPromise;
}

export function createYouTubePlayer(url, id) {
    let player;
    let events = {};
    let rafId = null;
    let duration = null;
    let realDuration = null;

    const _emit = (event, data) => {
        events[event]?.forEach(cb => cb(data));
    };

    const _startUpdate = () => {
        const tick = () => {
            if (!player) {
                rafId = null;
                return;
            }

            let currentTime = player.getCurrentTime();

            if (!realDuration) {
                currentTime *= 1.075;
            }

            const effectiveDuration = realDuration || duration;
            const percent = Math.min(currentTime / effectiveDuration * 100, 100);

            _emit('progress', percent);
            rafId = requestAnimationFrame(tick); // eslint-disable-line compat/compat
        };

        if (!rafId) {
            rafId = requestAnimationFrame(tick); // eslint-disable-line compat/compat
        }
    };

    const _stopUpdate = () => {
        if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
    };

    return {
        async mount(container) {
            await loadYouTubeAPI();

            const dummy = document.createElement('div');
            container.appendChild(dummy);

            return new Promise(resolve => { // eslint-disable-line compat/compat
                player = new YT.Player(dummy, { // eslint-disable-line no-undef
                    videoId: id,
                    playerVars: {
                        controls: 0,
                        autoplay: 1,
                        mute: 1,
                        disablekb: 1,
                        enablejsapi: 1,
                        iv_load_policy: 3,
                        playsinline: 1,
                        rel: 0,
                        fs: 0,
                        cc_load_policy: 0,
                        origin: window.location.origin,
                    },
                    events: {
                        onReady: () => {
                            duration = player.getDuration();
                            _emit('loaded');
                            resolve();
                        },
                        onStateChange: event => {
                            if (event.data === YT.PlayerState.ENDED) { // eslint-disable-line no-undef
                                if (!realDuration) {
                                    realDuration = player.getCurrentTime();
                                }
                                player.stopVideo();
                                _emit('progress', 100);
                                _emit('ended');
                                _stopUpdate();
                            } else if (event.data === YT.PlayerState.PLAYING) { // eslint-disable-line no-undef
                                _emit('play');
                                _startUpdate();
                            } else if (event.data === YT.PlayerState.PAUSED) { // eslint-disable-line no-undef
                                _emit('pause');
                                _stopUpdate();
                            }
                        },
                    },
                });
            });
        },

        async play() {
            player.playVideo();
        },

        async pause() {
            player.pauseVideo();
        },

        async stop() {
            player.pauseVideo();
            player.seekTo(0);
            _emit('progress', 0);
        },

        async reset() {
            player.seekTo(0);
            _emit('progress', 0);
        },

        async isPlaying() {
            return player.getPlayerState() === YT.PlayerState.PLAYING; // eslint-disable-line no-undef
        },

        on(event, callback) {
            (events[event] ||= []).push(callback);
        },

        off(event, callback) {
            if (!events[event]) {
                return;
            }
            events[event] = events[event].filter(cb => cb !== callback);
        },
    };
}

export function createVimeoPlayer(url) {
    let player;
    let events = {};
    let rafId = null;
    let duration = null;
    let realDuration = null;

    const _emit = (event, data) => {
        events[event]?.forEach(cb => cb(data));
    };

    const _startUpdate = () => {
        const tick = async () => {
            if (!player) {
                rafId = null;
                return;
            }

            let currentTime = await player.getCurrentTime();

            if (!realDuration) {
                currentTime *= 1.075;
            }

            const effectiveDuration = realDuration || duration;
            const percent = Math.min(currentTime / effectiveDuration * 100, 100);

            _emit('progress', percent);
            rafId = requestAnimationFrame(tick); // eslint-disable-line compat/compat
        };

        if (!rafId) {
            rafId = requestAnimationFrame(tick); // eslint-disable-line compat/compat
        }
    };

    const _stopUpdate = () => {
        if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
        }
    };

    return {
        async mount(container) {
            return new Promise(resolve => { // eslint-disable-line compat/compat
                player = new Player(container, {
                    url,
                    background: false,
                    controls: false,
                    muted: true,
                    autoplay: false,
                    preload: 'auto',
                    rel: 0,
                    loop: parseBoolean(container.dataset.loop),
                });

                player.ready().then(async () => {
                    player.setLoop(parseBoolean(container.dataset.loop));
                });

                player.getDuration().then(value => {
                    duration = value;
                });

                player.on('loaded', () => {
                    _emit('loaded');
                    resolve();
                });

                player.on('play', () => {
                    _emit('play');
                    _startUpdate();
                });

                player.on('pause', () => {
                    _emit('pause');
                    _stopUpdate();
                });

                player.on('ended', data => {
                    if (!realDuration) {
                        realDuration = data.duration;
                    }
                    _emit('progress', 100);
                    _emit('ended');
                    _stopUpdate();
                });
            });
        },

        async play() {
            await player.play();
        },

        async pause() {
            await player.pause();
        },

        async stop() {
            await player.pause();
            await player.setCurrentTime(0);
            _emit('progress', 0);
        },

        async reset() {
            await player.setCurrentTime(0);
            _emit('progress', 0);
        },

        async isPlaying() {
            return player.getPaused().then(isPaused => !isPaused);
        },

        on(event, callback) {
            (events[event] ||= []).push(callback);
        },

        off(event, callback) {
            if (!events[event]) {
                return;
            }
            events[event] = events[event].filter(cb => cb !== callback);
        },
    };
}

const PROVIDERS = {
    youtube: { createPlayer: createYouTubePlayer },
    vimeo: { createPlayer: createVimeoPlayer },
};

export function determineService(url) {
    const { id, service } = getVideoId(url);
    const provider = PROVIDERS[service];

    if (!provider) {
        return null;
    }

    return {
        id,
        service,
        player() {
            return provider.createPlayer(url, id);
        },
    };
}

export function initialiseVideo(element) {
    const videoContainer = element.querySelector('.video-container');

    if (!videoContainer) {
        return;
    }

    const provider = determineService(videoContainer.dataset.videoUrl);

    if (!provider) {
        return;
    }

    const autoplay = parseBoolean(videoContainer.dataset.autoplay, true);
    const playButtons = [...element.querySelectorAll('.home-hero__stop-autoplay')];
    let playState = null;

    const toggleButtonState = isPlaying => {
        if (playState === isPlaying) {
            return;
        }

        playState = isPlaying;

        for (const btn of playButtons) {
            const icon = btn.querySelector('.home-hero__icon');
            const label = btn.querySelector('.home-hero__label');

            if (isPlaying) {
                icon.innerHTML = '<svg class="o-icon o-icon--pause"><use xlink:href="#pause"></use></svg>';
                label.textContent = 'Pause';
            } else {
                icon.innerHTML = '<svg class="o-icon o-icon--play"><use xlink:href="#play"></use></svg>';
                label.textContent = 'Play';
            }
        }
    };

    const player = provider.player();

    return player.mount(videoContainer).then(() => {
        for (const btn of playButtons) {
            btn.addEventListener('click', async () => {
                (await player.isPlaying()) ? player.pause() : player.play();
            });
        }

        let initialPlay = true;

        player.on('play', () => {
            if (initialPlay) {
                videoContainer.classList.add('is-loaded');
                element.classList.add('is-loaded');
                initialPlay = false;
            }
            toggleButtonState(true);
        });

        player.on('pause', () => {
            toggleButtonState(false);
        });

        if (!prefersReducedMotion && autoplay) {
            player.play();
        }

        return player;
    });
}

export function init() {
    document.querySelectorAll('.js-header-video').forEach(initialiseVideo);
}
